import React, { useEffect, useMemo, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import { toast } from "react-toastify";
import withAuth from "../../utils/withAuth";
import { fetchProcessDetails } from "../../utils/api";
import { Table } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";

/** Format a JS Date to DD-MM-YYYY in IST */
const toIST_DDMMYYYY = (d) => {
    const dt = new Date(d.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
};

/** ISO key (YYYY-MM-DD) from a JS Date in IST */
const toIST_ISODateKey = (d) => {
    const dt = new Date(d.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

/** Build last 7 calendar days array (today back to 6 days ago) */
const useLast7Days = () =>
    useMemo(() => {
        const days = [];
        const now = new Date();
        for (let i = 0; i < 7; i++) {
            const d = new Date(now);
            d.setDate(now.getDate() - i);
            days.push(d);
        }
        return days; // [today, yesterday, ...]
    }, []);

/** Build a list of ISO dates between startISO and endISO (inclusive), in IST */
const buildISODateList = (startISO, endISO) => {
    const out = [];
    // Force parse with IST offset to avoid TZ drift
    const start = new Date(`${startISO}T00:00:00+05:30`);
    const end = new Date(`${endISO}T00:00:00+05:30`);
    const cur = new Date(start);
    while (cur <= end) {
        out.push(toIST_ISODateKey(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return out;
};

const extractRowsToMap = (data) => {
    const rowsArr =
        data?.rows ||
        data?.process?.rows ||
        (Array.isArray(data?.data) ? data?.data : data?.data?.rows) ||
        [];

    if (!Array.isArray(rowsArr)) return {};

    const out = {};
    for (const r of rowsArr) {
        const iso = r?.date_iso || r?.date || r?.day || r?.DateISO || r?.Date || "";
        if (!iso) continue;
        out[iso] = {
            calls: Number(r?.calls ?? 0),
            totalorder: Number(r?.totalorder ?? 0),
            spend: Number(r?.spend ?? 0),
            impress: Number(r?.impress ?? 0),
            clicks: Number(r?.clicks ?? 0),
            cpm: Number(r?.cpm ?? 0),
            cpc: Number(r?.cpc ?? 0),
            ctr: Number(r?.ctr ?? 0),
            call_cost: Number(r?.call_cost ?? 0),
        };
    }
    return out;
};

const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN");
const fmt2 = (n) =>
    Number(n || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
const fmt4 = (n) =>
    Number(n || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
    });

const ProcessDetailReport = () => {
    const { slug = "" } = useParams();
    const ppcid = localStorage.getItem("ppcdata");
    const last7Days = useLast7Days();

    const [daily, setDaily] = useState({});
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [processMeta, setProcessMeta] = useState({}); // { process_skill_name, process_mobile }

    // Default range: last 7 days (inclusive)
    const defaultStartISO = toIST_ISODateKey(last7Days[last7Days.length - 1]);
    const defaultEndISO = toIST_ISODateKey(last7Days[0]);

    const [dateRange, setDateRange] = useState({
        startISO: defaultStartISO,
        endISO: defaultEndISO,
    });

    // Button label for the picker
    const [label, setLabel] = useState(
        `${moment(defaultStartISO).format("DD MMM YYYY")} - ${moment(defaultEndISO).format("DD MMM YYYY")}`
    );

    // Fetch whenever slug or date range changes
    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setErr("");
            try {
                const body = { slug, ppcid, startISO: dateRange.startISO, endISO: dateRange.endISO };
                const res = await fetchProcessDetails(
                    "ppc-team-get-process-details-report-last-week",
                    body
                );

                if (res?.data?.status === 1) {
                    const map = extractRowsToMap(res?.data);
                    if (mounted) {
                        setDaily(map);
                        // Backend should return: { process: { process_skill_name, process_mobile }, rows: [...] }
                        setProcessMeta(res?.data?.process.process || {});
                    }
                } else {
                    const msg = res?.data?.message || "Failed to load process";
                    toast.error(msg);
                    if (mounted) setErr(msg);
                }
            } catch (e) {
                console.error(e);
                toast.error("Network/CORS error while loading process");
                if (mounted) setErr("Network/CORS error while loading process");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [slug, ppcid, dateRange.startISO, dateRange.endISO]);

    /** DateRangePicker -> onApply */
    const handleFilterByDate = (event, picker) => {
        const s = moment(picker.startDate).format("YYYY-MM-DD");
        const e = moment(picker.endDate).format("YYYY-MM-DD");
        setDateRange({ startISO: s, endISO: e });
        setLabel(`${moment(s).format("DD MMM YYYY")} - ${moment(e).format("DD MMM YYYY")}`);
    };

    /** Clear to default last-7-days */
    const handleClear = () => {
        setDateRange({ startISO: defaultStartISO, endISO: defaultEndISO });
        setLabel(
            `${moment(defaultStartISO).format("DD MMM YYYY")} - ${moment(defaultEndISO).format("DD MMM YYYY")}`
        );
    };

    /** Build table rows for currently selected date range (newest → oldest) */
    const isoList = buildISODateList(dateRange.startISO, dateRange.endISO).reverse();
    const rows = isoList.map((iso) => {
        // turn ISO back to a Date (IST) only for display formatting
        const displayDate = toIST_DDMMYYYY(new Date(`${iso}T00:00:00+05:30`));
        const rec =
            daily[iso] || {
                calls: 0,
                totalorder: 0,
                spend: 0,
                impress: 0,
                clicks: 0,
                cpm: 0,
                cpc: 0,
                ctr: 0,
            };
        const cpm = rec.cpm || (rec.impress > 0 ? (rec.spend / rec.impress) * 1000 : 0);
        const cpc = rec.cpc || (rec.clicks > 0 ? rec.spend / rec.clicks : 0);
        const ctr = rec.ctr || (rec.impress > 0 ? rec.clicks / rec.impress : 0);

        return {
            displayDate,
            calls: Number(rec.calls || 0),
            totalorder: Number(rec.totalorder || 0),
            spend: Number(rec.spend || 0),
            impress: Number(rec.impress || 0),
            clicks: Number(rec.clicks || 0),
            cpm: Number(cpm || 0),
            cpc: Number(cpc || 0),
            ctr: Number(ctr || 0),
        };
    });

    // Totals (and derived CPM/CPC/CTR on totals)
    const totals = rows.reduce(
        (acc, r) => {
            acc.calls += r.calls;
            acc.totalorder += r.totalorder;
            acc.spend += r.spend;
            acc.impress += r.impress;
            acc.clicks += r.clicks;
            return acc;
        },
        { calls: 0, totalorder: 0, spend: 0, impress: 0, clicks: 0 }
    );
    const totalsCPM = totals.impress > 0 ? (totals.spend / totals.impress) * 1000 : 0;
    const totalsCPC = totals.clicks > 0 ? totals.spend / totals.clicks : 0;
    const totalsCTR = totals.impress > 0 ? totals.clicks / totals.impress : 0;

    const hasProcess =
        !!processMeta?.process_skill_name || !!processMeta?.process_mobile;

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR::PROCESS REPORT</title>
            </Helmet>
            <ContentWrapper>
                <div className="row">
                    <div className="card hrpages">
                        <div className="col-12">
                            <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
                                <h5 className="mb-0" style={{ fontWeight: "600" }}>
                                    {moment(dateRange.startISO).format("DD MMM YYYY")} to{" "}
                                    {moment(dateRange.endISO).format("DD MMM YYYY")}
                                </h5>

                                {loading ? (
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="placeholder col-6 rounded-pill" style={{ height: 24 }} />
                                        <span className="placeholder col-6 rounded-pill" style={{ height: 24 }} />
                                    </div>
                                ) : hasProcess ? (
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className="badge bg-secondary">
                                            Process: {processMeta?.process_skill_name}
                                        </span>
                                        <span className="badge bg-secondary">
                                            Display Number: {processMeta?.process_mobile}
                                        </span>
                                    </div>
                                ) : null}

                                <div className="d-flex gap-2">
                                    <DateRangePicker
                                        initialSettings={{
                                            startDate: moment(dateRange.startISO, "YYYY-MM-DD"),
                                            endDate: moment(dateRange.endISO, "YYYY-MM-DD"),
                                            showDropdowns: true,
                                            alwaysShowCalendars: true,
                                            locale: { format: "DD MMM YYYY" },
                                        }}
                                        onApply={handleFilterByDate}
                                    >
                                        <button className="btn btn-sm btndaterange" type="button">
                                            {label}
                                        </button>
                                    </DateRangePicker>

                                    <button
                                        className="btn btn-sm btn-reset"
                                        type="button"
                                        onClick={handleClear}
                                        disabled={loading}
                                        title="Reset to last 7 days"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>

                            {err ? <div className="alert alert-danger py-2">{err}</div> : null}

                            <div className="table-responsive">
                                <Table className="table-sm table-fixeds" responsive striped bordered hover>
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ whiteSpace: "nowrap" }}>Date</th>
                                            <th style={{ whiteSpace: "nowrap" }}>Total Call</th>
                                            <th style={{ whiteSpace: "nowrap" }}>Total Order</th>
                                            <th style={{ whiteSpace: "nowrap" }}>Total Spend</th>
                                            <th style={{ whiteSpace: "nowrap" }}>Total Impressions</th>
                                            <th style={{ whiteSpace: "nowrap" }}>Total Clicks</th>
                                            <th style={{ whiteSpace: "nowrap" }}>CPM</th>
                                            <th style={{ whiteSpace: "nowrap" }}>CPC</th>
                                            <th style={{ whiteSpace: "nowrap" }}>CTR</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading
                                            ? Array.from({ length: Math.max(7, isoList.length || 7) }).map((_, i) => (
                                                <tr key={`skeleton-${i}`}>
                                                    {Array.from({ length: 9 }).map((__, j) => (
                                                        <td key={`sk-${i}-${j}`}>
                                                            <div className="placeholder-glow">
                                                                <span className="placeholder col-6" />
                                                            </div>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                            : rows.length === 0 ? (
                                                <tr>
                                                    <td colSpan={9} className="text-center text-muted py-4">
                                                        No data for the selected period.
                                                    </td>
                                                </tr>
                                            ) : (
                                                rows.map((r) => (
                                                    <tr key={r.displayDate}>
                                                        <td>{r.displayDate}</td>
                                                        <td>{fmt0(r.calls)}</td>
                                                        <td>{fmt0(r.totalorder)}</td>
                                                        <td>{fmt2(r.spend)}</td>
                                                        <td>{fmt0(r.impress)}</td>
                                                        <td>{fmt0(r.clicks)}</td>
                                                        <td>{fmt4(r.cpm)}</td>
                                                        <td>{fmt2(r.cpc)}</td>
                                                        <td>{fmt4(r.ctr)}</td>
                                                    </tr>
                                                ))
                                            )}
                                    </tbody>

                                    {!loading && rows.length > 0 && (
                                        <tfoot>
                                            <tr className="fw-semibold">
                                                <td style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>Total</td>
                                                <td style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>{fmt0(totals.calls)}</td>
                                                <td style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>{fmt0(totals.totalorder)}</td>
                                                <td style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>{fmt2(totals.spend)}</td>
                                                <td style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>{fmt0(totals.impress)}</td>
                                                <td style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>{fmt0(totals.clicks)}</td>
                                                <td style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>{fmt4(totalsCPM)}</td>
                                                <td style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>{fmt2(totalsCPC)}</td>
                                                <td style={{ fontWeight: 700, fontSize: 14, letterSpacing: "0.8px" }}>{fmt4(totalsCTR)}</td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(ProcessDetailReport);
